import { createCSSClass, waitForRender } from "../../../core/js/utilities";
import { prefix } from "../../../core/design-tokens/meta";
import { dispatchEvent, renderState } from "../../../core/js/state-machine";
import {
    views,
    componentClasses,
    icons,
    componentName,
} from "../../../core/components/accordion/accordion-item";
/**
 * Accordion-Item must be used only by Accordion.
 * @param {HTMLElement} element
 * @param {Object} props from Accordion
 * @returns {Object} api
 */
const AccordionItem = (element, props = {}) => {
    const { index, expand, collapse, eventMap, wrapper, expandedStore, focusStore } = props;

    // Call the open/close from Accordion public interface
    const handleClick = (event) => {
        event.preventDefault();
        // Get current value from state machine + current item
        const isExpanded = element.classList.contains(componentClasses.expanded);
        isExpanded ? collapse(index) : expand(index);
    };

    const handleFocus = (focusList) => {
        const hasFocus = focusList[index];
        renderState(hasFocus ? views.wrapper.focus : views.wrapper.rest, element);
    };

    // Captures the height of the content
    // creates a dynamic class and wait for
    // the transition to finish
    const startExpanding = () => {
        dispatchEvent(eventMap.expanding, wrapper, { index, element });
        const heightToExpand = bodyElement.clientHeight;
        const expandStyle = { height: `${heightToExpand}px !important` };
        dynamicExpanding = `${prefix}dynamic-accordion-expanding-height-${heightToExpand}`;

        // Failed to create dynamic class - skip animation and set it opened.
        if (!createCSSClass(`.${dynamicExpanding}`, expandStyle)) {
            endExpanding();
            return;
        }

        contentElement.addEventListener("transitionend", endExpanding);
        contentElement.addEventListener("transitioncancel", endExpanding);

        const classList = [dynamicExpanding];
        classList.push(`!${dynamicCollapsing}`);
        renderState({ classList }, contentElement);
        renderState(views.wrapper.expanding, element);
        renderState(views.button.expanded, buttonElement);
    };

    // Apply classes to wrapper and content
    // to ensure aria and the correct styling
    // and dispatches the event
    const endExpanding = (event) => {
        if (event.target !== contentElement || event.propertyName !== "height") {
            return;
        }

        disposeTransitions();

        // Inject the dynamic class into existent view def.
        const classList = [`!${dynamicExpanding}`];
        const attrs = Object.assign({}, views.content.expanded.attrs);
        renderState(views.wrapper.expanded, element);
        renderState({ classList, attrs }, contentElement);
        dispatchEvent(eventMap.expanded, wrapper, { index, element });
    };

    // Apply fixed height and update aria-*
    // before adding the collapsing
    const startCollapsing = () => {
        const heightToCollapse = bodyElement.clientHeight;
        const collapsingStyle = {
            height: `${heightToCollapse}px !important`,
            "transition-duration": "0s !important",
        };
        dynamicCollapsing = `${prefix}dynamic-accordion-collapsing-height-${heightToCollapse}`;

        if (!createCSSClass(`.${dynamicCollapsing}`, collapsingStyle)) {
            console.warn("Failed to create dynamic class", dynamicCollapsing);
            endCollapsing({ target: contentElement, propertyName: "height" });
            return;
        }
        // 1st - apply dyn class with fixed height and 0s of animation.
        // we need to wait until it takes effect before applying the
        // animation to collapse - it won't fire transition events.
        const fixedHeightStyle = { classList: [dynamicCollapsing] };
        renderState(fixedHeightStyle, contentElement);
        waitForRender(contentElement, "transitionDuration", "0s")
            .then(() => {
                // 2rd part is to set the height to be 0
                // and wait for the animation to finish
                const classList = [`!${dynamicCollapsing}`, `!${dynamicExpanding}`];
                contentElement.addEventListener("transitionend", endCollapsing);
                contentElement.addEventListener("transitioncancel", endCollapsing);
                renderState(views.button.collapsed, buttonElement);
                renderState(views.wrapper.collapsing, element);
                renderState({ classList }, contentElement);
                dispatchEvent(eventMap.collapsing, wrapper, { index, element });
            })
            .catch((err) => {
                console.warn(err);
                // Force the end of the animation
                // adding a mock event
                endCollapsing({ target: contentElement, propertyName: "height" });
            });
    };

    // Update wrapper, aria and dispatches the event
    const endCollapsing = (event) => {
        if (event.target !== contentElement || event.propertyName !== "height") {
            return;
        }

        disposeTransitions();

        // Inject the dynamic class into existent view def.
        const attrs = views.content.collapsed.attrs;
        const classList = [`!${dynamicCollapsing}`];
        renderState({ attrs, classList }, contentElement);
        renderState(views.wrapper.collapsed, element);
        dispatchEvent(eventMap.collapsed, wrapper, {
            index,
            element,
        });
    };

    // Remove transition events from contentElement
    const disposeTransitions = () => {
        contentElement.removeEventListener("transitionend", endCollapsing);
        contentElement.removeEventListener("transitioncancel", endCollapsing);
        contentElement.removeEventListener("transitionend", endExpanding);
        contentElement.removeEventListener("transitioncancel", endExpanding);
    };

    // Dispose all event listeners
    const dispose = () => {
        disposeTransitions();
        buttonElement.removeEventListener("click", handleClick);
    };

    // Update the accordion list according to the the state.
    // and dispatches the DOM events
    const handleStateChange = (newStateList) => {
        //Capture new state for this instance!
        const shouldExpand = newStateList[index] === true;
        if (shouldExpand === currentState || isInactive) {
            return;
        }
        currentState = shouldExpand;
        disposeTransitions();
        shouldExpand ? startExpanding() : startCollapsing();
    };

    const buttonElement = element.querySelector(`.${componentClasses.button}`);
    const contentElement = element.querySelector(`.${componentClasses.content}`);
    const bodyElement = element.querySelector(`.${componentClasses.body}`);

    if (!buttonElement || !contentElement || !bodyElement) {
        return console.error("Missing required elements");
    }

    const isInactive =
        buttonElement.hasAttribute("disabled") ||
        element.classList.contains(componentClasses.inactive);

    const api = { dispose, element, index, isInactive, buttonElement };

    let dynamicExpanding = "";
    let dynamicCollapsing = "";
    let currentState = element.classList.contains(componentClasses.expanded);

    expandedStore.subscribe(handleStateChange);
    focusStore.subscribe(handleFocus);

    buttonElement.addEventListener("click", handleClick);
    buttonElement.insertAdjacentHTML("beforeend", icons.chevron({ class: componentClasses.icon }));

    if (isInactive) {
        renderState(views.wrapper.inactive, element);
        renderState(views.button.inactive, buttonElement);
    }

    element[componentName] = api;
    return api;
};
export default AccordionItem;
